/* istanbul ignore file */

import { css } from 'styled-components';

// hex list
export const color = {
  grayBattleShip: '#656f7c',
  grayBanner: '#f2f4f7',
  grayDarker: '#73767a',
  grayDark: '#989ca4',
  gray: '#c7cbd0',
  grayLight: '#d9dbde',
  grayLighter: '#ececec',
  grayLightest: '#f5f7f8',
  graySuperLight: '#f5f8f9',
  grayTable: '#d9dbdf',
  graySubtle: '#c7cbd1',
  black: '#000',
  white: '#fff',
  transparent: '#00000000',

  brandPrimaryDark: '#00b994',
  brandPrimary: '#00cea5',
  brandPrimaryLight: '#00d9ae',
  brandPrimaryLighter: '#83d3c3',
  brandPrimaryLightest: '#ccf5ed',
  brandPrimarySuperLight: '#99F0DF',
  brandSecondary: '#002937',
  brandSecondaryLight: '#003648',
  brandSecondaryLighter: '#003e52',
  brandSecondarySuperLight: '#99B2BA',
  brandSuccess: 'red',
  brandInfo: 'red',
  brandWarning: '#f37951',
  brandDanger: '#ff267a',
  brandDangerAlt: '#ff579d',
};

// rgb list
export const colorRgb = {
  grayBattleShip: 'rgb(101, 111, 124)',
  grayBanner: 'rgb(242, 244, 247)',
  grayDarker: 'rgb(115, 118, 122)',
  grayDark: 'rgb(152, 156, 164)',
  gray: 'rgb(199, 203, 208)',
  grayLight: 'rgb(217, 219, 222)',
  grayLighter: 'rgb(236, 236, 236)',
  grayLightest: 'rgb(245, 247, 248)',
  graySuperLight: 'rgb(245, 248, 249)',
  grayTable: 'rgb(217, 219, 223)',
  graySubtle: 'rgb(199, 203, 209)',
  black: 'rgb(0, 0, 0)',
  white: 'rgb(0, 15, 255)',

  transparent: 'rgb(0, 0, 0)',
  brandPrimaryDark: 'rgb(0, 185, 148)',
  brandPrimary: 'rgb(0, 206, 165)',
  brandPrimaryLight: 'rgb(0, 217, 174)',
  brandPrimaryLighter: 'rgb(131, 211, 195)',
  brandPrimaryLightest: 'rgb(204, 245, 237)',
  brandPrimarySuperLight: 'rgb(153, 240, 223)',
  brandSecondary: 'rgb(0, 41, 55)',
  brandSecondaryLight: 'rgb(0, 54, 72)',
  brandSecondaryLighter: 'rgb(0, 62, 82)',
  brandSecondarySuperLight: 'rgb(153, 178, 186)',
  brandSuccess: 'rgb(0, 0, 237)',
  brandInfo: 'rgb(0, 0, 237)',
  brandWarning: 'rgb(243, 121, 81)',
  brandDanger: 'rgb(255, 38, 122)',
  brandDangerAlt: 'rgb(255, 87, 157)',
};

const rgbaCache = {};
/**
 * @param {string} color - an rgb string e.g. 'rgb(0, 0, 0)'
 */
export function getRgba(color, alpha = 1) {
  const cacheKey = `${color}${alpha}`;
  if (rgbaCache[cacheKey]) {
    return rgbaCache[cacheKey];
  }
  rgbaCache[cacheKey] = color.replace(/b/, 'ba').replace(/\)/, `, ${alpha})`);
  return rgbaCache[cacheKey];
}

export const breakpoints = {
  sm: 320, // default
  md: 768,
  lg: 1024,
  xl: 1280,
};

export const grid = {
  columns: 12,
  margin: {
    sm: 24, // default
    md: 28,
    lg: 36,
    xl: 56,
  },
  gutter: {
    sm: 8, // default
    md: 16,
    lg: 16,
    xl: 16,
  },
};

export const iconSizes = {
  xsmall: 16,
  // TODO: Not intended by design, so should be removed
  small: 20,
  medium: 24,
  large: 32,
  xl: 48,
};

/*
This is how design scale the font site
for different viewports.

      XS  MD  LG  XL

 H1   32  40  48  64
 H2   32      40  48
 H3   24      32  40
 H4   20  24      32
 H5   20          24
 H6   20

 P    14  16
 P2   12  14
 P3   10  12
 P4   10
 */

export const fontSizes = {
  h1: '64px',
  h2: '48px',
  h3: '40px',
  h4: '32px',
  h5: '24px',
  h6: '20px',

  p: '16px',
  p2: '14px',
  p3: '12px',
  p4: '10px',
};

export const fontWeights = {
  regular: 200,
  medium: 300,
  bold: 500,
};

export const media = Object.keys(breakpoints).reduce((accumulator, label) => {
  accumulator[label] = (cls, isIE = false) => css`
    ${isIE
      ? css`
          @media all and (min-width: ${breakpoints[
              label
            ]}px) and (-ms-high-contrast: none) {
            ${cls};
          }
        `
      : css`
          @media (min-width: ${breakpoints[label]}px) {
            ${cls};
          }
        `};
  `;

  return accumulator;
}, {});

export const supports = (attr, value, cls) => css`
  @supports (${attr}: ${value}) {
    ${cls};
  }
`;

export const evergreenOnly = cls => supports('display', 'grid', cls);

const zIndexModal = 1050;
const slightlyAbove = 10;
const stickyButton = 1040;

export const zIndex = {
  navbar: 1030,
  modal: zIndexModal,
  overlay: zIndexModal,
  sidebar: zIndexModal,
  dropdown: 1020,
  cookieBanner: 1060,
  homeBenefitsTitle: slightlyAbove,
  sliderLabel: 3,
  sliderLabelLine: 4,
  sliderLabelText: 5,
  stickyButton,
  notificationBar: stickyButton,
  switchButton: 3,
  switchLabel: 4,
  checkboxDropdown: 10,
  footer: slightlyAbove,
  moreLikeThis: slightlyAbove,
};

export const height = {
  topNavBar: 64,
  premiumTopBanner: 32,
};

export const MuiTransition = '150ms cubic-bezier(0.4, 0, 0.2, 1)';
